import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Loader2, Phone } from "lucide-react";
import { toast } from "sonner";
import SEO, { pageSEO } from "../components/SEO";
import { useAuth } from "../App";

const AuthPage = ({ isLogin }) => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const { login, register, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate(user.role === "admin" ? "/admin" : "/dashboard");
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userData = isLogin 
        ? await login(formData.email)
        : await register(formData.name, formData.email, formData.phone);
      toast.success(isLogin ? "Welcome back!" : "Account created successfully!");
      navigate(userData.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    }
    setLoading(false);
  };

  return (
    <>
      <SEO {...(isLogin ? pageSEO.login : pageSEO.register)} />
      <div className="min-h-screen bg-slate-950 flex">
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600/20 to-slate-950 items-center justify-center p-12">
          <div className="text-center max-w-md">
            <Link to="/" className="hover:opacity-80 transition-opacity inline-block mb-8">
              <h1 className="text-5xl font-bold text-white mb-6">
                Smart<span className="text-blue-500">IT</span>
              </h1>
              <p className="text-xl text-slate-300">Your trusted IT partner in Aylesbury</p>
            </Link>
            
            <p className="text-slate-400 text-base mb-8 leading-relaxed">
              SmartIT provides reliable computer repairs, IT support, diagnostics, and technical solutions for both home and business clients.
            </p>
            
            <ul className="text-left space-y-3 mb-8">
              <li className="flex items-center gap-3 text-slate-300">
                <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                <span>Fast and reliable service</span>
              </li>
              <li className="flex items-center gap-3 text-slate-300">
                <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                <span>Secure customer dashboard</span>
              </li>
              <li className="flex items-center gap-3 text-slate-300">
                <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                <span>Real-time repair updates</span>
              </li>
              <li className="flex items-center gap-3 text-slate-300">
                <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                <span>Priority technical support</span>
              </li>
            </ul>
            
            <p className="text-slate-500 text-sm">
              Trusted by clients across Aylesbury and Buckinghamshire.
            </p>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center p-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md"
          >
            <Link to="/" className="lg:hidden text-center mb-8 block hover:opacity-80 transition-opacity">
              <h1 className="text-3xl font-bold text-white">
                Smart<span className="text-blue-500">IT</span>
              </h1>
            </Link>

            <div className="glass rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-white mb-2">
                {isLogin ? "Welcome back" : "Create account"}
              </h2>
              <p className="text-slate-400 mb-8">
                {isLogin ? "Sign in to access your dashboard" : "Sign up to submit support tickets"}
              </p>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid={isLogin ? "login-form" : "register-form"}>
                {!isLogin && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Full Name</label>
                      <input
                        type="text"
                        required
                        className="input"
                        placeholder="Your full name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        data-testid="register-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Telephone Number</label>
                      <input
                        type="tel"
                        required
                        className="input"
                        placeholder="07123 456789"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        data-testid="register-phone"
                      />
                    </div>
                  </>
                )}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
                  <input
                    type="email"
                    required
                    className="input"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid={isLogin ? "login-email" : "register-email"}
                  />
                </div>
                <button type="submit" className="btn-primary w-full justify-center" disabled={loading} data-testid={isLogin ? "login-submit" : "register-submit"}>
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (isLogin ? "Sign In" : "Create Account")}
                </button>
              </form>

              <p className="text-center text-slate-400 mt-6">
                {isLogin ? "Don't have an account? " : "Already have an account? "}
                <Link to={isLogin ? "/register" : "/login"} className="text-blue-500 hover:text-blue-400">
                  {isLogin ? "Sign up" : "Sign in"}
                </Link>
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default AuthPage;
